import { useEffect, useRef } from 'react';

interface Props {
  /** valores diários (mais recente por último) */
  valores: number[];
  width?: number;
  height?: number;
}

export function RevenueSparkline({ valores, width = 720, height = 160 }: Props) {
  const pathRef = useRef<SVGPathElement | null>(null);
  const areaRef = useRef<SVGPathElement | null>(null);
  useEffect(() => {
    const p = pathRef.current;
    if (!p) return;
    try {
      const len = p.getTotalLength();
      p.style.transition = 'none';
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
      p.getBoundingClientRect();
      p.style.transition = 'stroke-dashoffset 1400ms cubic-bezier(0.16, 1, 0.3, 1)';
      p.style.strokeDashoffset = '0';
    } catch {
      /* getTotalLength pode falhar em SSR */
    }
    if (areaRef.current) {
      const a = areaRef.current;
      a.style.transition = 'none';
      a.style.opacity = '0';
      a.getBoundingClientRect();
      a.style.transition = 'opacity 600ms ease-out 800ms';
      a.style.opacity = '1';
    }
  }, [valores]);

  if (valores.length === 0) return null;
  const max = Math.max(...valores);
  const min = Math.min(...valores);
  const range = Math.max(1, max - min);
  const stepX = width / (valores.length - 1 || 1);

  const pontos = valores.map((v, i) => ({
    x: i * stepX,
    y: height - ((v - min) / range) * (height - 24) - 12,
  }));
  const d = pontos.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const dArea = `${d} L${width} ${height} L0 ${height} Z`;
  const ultimo = pontos[pontos.length - 1];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[160px]" preserveAspectRatio="none">
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(149,184,134,0.28)" />
          <stop offset="100%" stopColor="rgba(149,184,134,0)" />
        </linearGradient>
      </defs>
      <path ref={areaRef} d={dArea} fill="url(#spark-fill)" />
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="var(--moss-300)"
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{ filter: 'drop-shadow(0 0 6px rgba(149,184,134,0.35))' }}
      />
      <circle
        cx={ultimo.x}
        cy={ultimo.y}
        r={3.5}
        fill="var(--moss-300)"
        style={{ filter: 'drop-shadow(0 0 8px rgba(149,184,134,0.6))' }}
      >
        <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

export default RevenueSparkline;
